import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Species, type BookingSessionProvider } from '@bondvet/types/booking';
import { LocationWithCoordinates } from 'lib/types';
import { gql } from '@apollo/client';
import { BOOKING_URL, IS_BROWSER } from 'core/constants';
import {
    setStepValidated,
    selectCity,
    selectLocation,
    selectDate,
    selectTime,
    setClientId,
    setEmail,
    setFirstName,
    setLastName,
    setPhoneNumber,
    setAnimalId,
    setPet,
    setSpecies,
    setReason,
    setNotes,
    selectProvider,
    setSmsPreferences,
    setSmsPreferencesDirty,
} from 'booking/redux/actions';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import useAvailableLocations from 'core/hooks/useAvailableLocations';
import {
    query as contactInfoQuery,
    PatientsAndContactInfoQuery,
    UsePatientsAndContactInfoProps as PatientsAndContactInfoVariables,
} from './usePatientsAndContactInfoQuery';
import useLazyBookingQuery from './useLazyBookingQuery';

interface RestoreBookingSessionQuery {
    restoreBookingSession: {
        _id: string;
        userId: string;
        email: string;
        firstName: string;
        lastName: string;
        phoneNumber: string;
        cityId: string;
        date: string;
        time: string;
        locationId: string;
        providerId: string;
        animalId: string;
        petName: string;
        petSpecies: Species;
        petAge: string;
        reason: string;
        notes: string;
        provider: null | BookingSessionProvider;
        smsMarketing: boolean;
        smsTransactional: boolean;
        smsConversational: boolean;
    };
}

interface RestoreBookingSessionVariables {
    sessionId: string;
}

const query = gql`
    query restoreBookingSession($sessionId: String!) {
        restoreBookingSession(sessionId: $sessionId) {
            _id
            userId
            email
            firstName
            lastName
            phoneNumber
            cityId
            date
            time
            locationId
            providerId
            animalId
            petName
            petSpecies
            petAge
            reason
            notes
            provider {
                id
                name
            }
            smsMarketing
            smsTransactional
            smsConversational
        }
    }
`;

interface RestoreBookingSessionType {
    isRestoring: boolean;
}

const routes = [
    `/${BOOKING_URL}/city`,
    `/${BOOKING_URL}/{citySlug}`,
    `/${BOOKING_URL}/{citySlug}/{locationSlug}`,
    `/${BOOKING_URL}/{citySlug}/{locationSlug}/client`,
    `/${BOOKING_URL}/{citySlug}/{locationSlug}/pet`,
    `/${BOOKING_URL}/{citySlug}/{locationSlug}/summary`,
];

function findCityIdAndSlugs(
    locations: ReadonlyArray<LocationWithCoordinates>,
    locationId: string | null,
): { cityId: string; citySlug: string; locationSlug: string } | null {
    const location = locations.find(
        (locationWithCoordinates) => locationWithCoordinates._id === locationId,
    );

    if (location?.city) {
        return {
            cityId: location.city.cityId,
            citySlug: location.city.slug,
            locationSlug: location.shortSlug ?? '',
        };
    }

    return null;
}

export default function useRestoreBookingSession(): RestoreBookingSessionType {
    const [isRestoring, setIsRestoring] = React.useState(IS_BROWSER);
    const dispatch = useDispatch();
    const location = useLocation();
    const locations = useAvailableLocations();

    const [loadRestoreBookingSession] = useLazyBookingQuery<
        RestoreBookingSessionQuery,
        RestoreBookingSessionVariables
    >(query);

    const [loadPatientsAndContactInfo] = useLazyBookingQuery<
        PatientsAndContactInfoQuery,
        PatientsAndContactInfoVariables
    >(contactInfoQuery);

    React.useEffect(() => {
        let isMounted = true;

        if (IS_BROWSER) {
            const params = new URLSearchParams(location.search);
            const sessionId = params.get('bookingSessionId');
            const emailParam = params.get('email');
            const locationIdParam = params.get('locationId');
            const patientId = params.get('patientId');
            const appointmentTypeId = params.get('appointmentTypeId');

            if (!emailParam && !sessionId && !locationIdParam) {
                setIsRestoring(false);
                return;
            }

            const navigateToLocation = (locationId: string) => {
                const cityIdAndSlugs = findCityIdAndSlugs(
                    locations,
                    locationId,
                );

                if (cityIdAndSlugs) {
                    const { cityId, citySlug, locationSlug } = cityIdAndSlugs;
                    dispatch(selectCity(cityId));
                    dispatch(setStepValidated('city', true));
                    dispatch(selectLocation(locationId || ''));
                    dispatch(setStepValidated('location', true));
                    dispatch(setEmail(emailParam || ''));
                    window.history.pushState(null, '', `/${BOOKING_URL}/city`);
                    window.history.pushState(
                        null,
                        '',
                        `/${BOOKING_URL}/${citySlug}`,
                    );

                    navigate(`/${BOOKING_URL}/${citySlug}/${locationSlug}`);
                } else {
                    navigate(`/${BOOKING_URL}/city`);
                }
            };

            const loadAndRestoreData = async () => {
                if (locationIdParam && !emailParam && isMounted) {
                    navigateToLocation(locationIdParam);
                    setIsRestoring(false);
                }
                if (emailParam) {
                    loadPatientsAndContactInfo({
                        variables: {
                            email: emailParam,
                        },
                    })
                        .then(({ data }) => {
                            if (isMounted) {
                                setIsRestoring(false);
                            }
                            if (data?.patientsAndContactInfo && isMounted) {
                                const {
                                    firstName,
                                    lastName,
                                    defaultLocation,
                                    _id: userId,
                                } = data.patientsAndContactInfo.contactInfo;
                                const { patients } =
                                    data.patientsAndContactInfo;
                                if (defaultLocation) {
                                    dispatch(setFirstName(firstName || ''));
                                    dispatch(setLastName(lastName || ''));
                                    dispatch(setClientId(userId));
                                    if (patientId) {
                                        dispatch(
                                            setAnimalId(
                                                patients.find(
                                                    (patient) =>
                                                        patient._vetspire.id ===
                                                        patientId,
                                                )?._id || '',
                                            ),
                                        );
                                    }
                                    if (appointmentTypeId) {
                                        dispatch(setReason(appointmentTypeId));
                                    }

                                    navigateToLocation(
                                        locationIdParam || defaultLocation,
                                    );
                                }
                            }
                        })
                        .catch((err) => {
                            console.warn(
                                'error restoring booking session',
                                err.message,
                            );
                            if (isMounted) {
                                setIsRestoring(false);
                            }
                        });
                }
                if (sessionId) {
                    loadRestoreBookingSession({
                        variables: {
                            sessionId,
                        },
                    })
                        .then(({ data }) => {
                            if (data?.restoreBookingSession && isMounted) {
                                const {
                                    cityId,
                                    locationId,
                                    date,
                                    time,
                                    userId,
                                    email,
                                    firstName,
                                    lastName,
                                    phoneNumber,
                                    animalId,
                                    petName,
                                    petSpecies,
                                    reason,
                                    notes,
                                    provider,
                                    smsMarketing,
                                    smsTransactional,
                                    smsConversational,
                                } = data.restoreBookingSession;

                                let cityIdAndSlugs: null | ReturnType<
                                    typeof findCityIdAndSlugs
                                > = null;

                                if (cityId) {
                                    dispatch(selectCity(cityId));
                                    dispatch(setStepValidated('city', true));
                                }
                                if (locationId) {
                                    dispatch(selectLocation(locationId));
                                    dispatch(
                                        setStepValidated('location', true),
                                    );

                                    cityIdAndSlugs = findCityIdAndSlugs(
                                        locations,
                                        locationId,
                                    );
                                }

                                if (provider?.id) {
                                    dispatch(
                                        selectProvider({
                                            ...provider,
                                            name: provider.name ?? '',
                                        }),
                                    );
                                }

                                if (date) {
                                    dispatch(selectDate(date));
                                }
                                if (time) {
                                    dispatch(selectTime(time));
                                    dispatch(setStepValidated('date', true));
                                }
                                if (userId) {
                                    dispatch(setClientId(userId));
                                }
                                if (email) {
                                    dispatch(setEmail(email));
                                }
                                if (firstName) {
                                    dispatch(setFirstName(firstName));
                                }
                                if (lastName) {
                                    dispatch(setLastName(lastName));
                                }
                                if (
                                    smsTransactional ||
                                    smsMarketing ||
                                    smsConversational
                                ) {
                                    dispatch(setSmsPreferences(true));
                                    dispatch(setSmsPreferencesDirty(true));
                                }
                                if (phoneNumber) {
                                    dispatch(setPhoneNumber(phoneNumber));
                                    dispatch(setStepValidated('client', true));
                                }
                                if (animalId) {
                                    dispatch(setAnimalId(animalId));
                                }
                                if (petName) {
                                    dispatch(setPet(petName));
                                }
                                if (petSpecies) {
                                    dispatch(setSpecies(petSpecies));
                                }
                                if (reason) {
                                    dispatch(setReason(reason));
                                }
                                if (notes) {
                                    dispatch(setNotes(notes));
                                    dispatch(setStepValidated('pet', true));
                                }

                                const routesToUse = [routes[0]];

                                if (cityIdAndSlugs) {
                                    const { citySlug, locationSlug } =
                                        cityIdAndSlugs;

                                    routesToUse.push(
                                        ...routes
                                            .slice(1)
                                            .map((route) =>
                                                route
                                                    .replace(
                                                        '{citySlug}',
                                                        citySlug,
                                                    )
                                                    .replace(
                                                        '{locationSlug}',
                                                        locationSlug,
                                                    ),
                                            ),
                                    );
                                }

                                let targetIndex = 5; // default to summary
                                if (!firstName || !lastName || !phoneNumber) {
                                    targetIndex = 3;
                                } else if (!animalId || !petName) {
                                    targetIndex = 4;
                                } else if (!time) {
                                    targetIndex = 2;
                                } else if (!locationId) {
                                    targetIndex = 1;
                                } else if (!cityId) {
                                    targetIndex = 0;
                                }

                                // Add previous routes to the browser history
                                for (let i = 0; i < targetIndex; i++) {
                                    window.history.pushState(
                                        null,
                                        '',
                                        routesToUse[i],
                                    );
                                }

                                // Navigate to the appropriate route
                                navigate(routesToUse[targetIndex]);
                            }
                            if (isMounted) {
                                setIsRestoring(false);
                            }
                        })
                        .catch((err) => {
                            // eslint-disable-next-line no-console
                            console.log('err', err);
                            if (isMounted) {
                                setIsRestoring(false);
                            }
                        });
                }
            };

            loadAndRestoreData().then();
        }

        // eslint-disable-next-line consistent-return
        return () => {
            isMounted = false;
        };
    }, [
        dispatch,
        loadRestoreBookingSession,
        location.search,
        location,
        loadPatientsAndContactInfo,
        locations,
        isRestoring,
    ]);
    return { isRestoring };
}
